import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import Modal from "../Modal"
import Button from "../Button"
import api from "../../services/api"

const UserFormModal = ({ isOpen, onClose, onSuccess, user: user = null, defaultOrganizationId = '' }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  })
  const [adminStatus, setAdminStatus] = useState('none')
  const [organizationId, setOrganizationId] = useState('')
  const [organizations, setOrganizations] = useState([])
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const isEditing = !!user

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
      })
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
      })
    }
    setAdminStatus('none')
    setOrganizationId(defaultOrganizationId ?? '')
    setErrors({})
    setSaving(false)
  }, [user, isOpen, defaultOrganizationId])

  // Organizations are only needed for the create form's org-admin option.
  useEffect(() => {
    if (!isOpen || isEditing) return

    api.getAdminOrganizations({ per_page: 200, sort_dir: 'asc' })
      .then((response) => setOrganizations(response.data.data ?? response.data))
      .catch(() => setErrors((prev) => ({ ...prev, organization_id: 'Failed to load organizations' })))
  }, [isOpen, isEditing])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required'
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required'
    }

    if (!isEditing && !formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    if (!isEditing && adminStatus === 'org' && !organizationId) {
      newErrors.organization_id = 'An organization is required for an org admin'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setSaving(true)

    try {
      if (isEditing) {
        await api.updateUser(user.id, formData)
      } else {
        await api.adminCreateUser({
          ...formData,
          is_admin: adminStatus !== 'none',
          is_super_admin: adminStatus === 'encircled',
          organization_id: organizationId || null,
        })
      }
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Failed to save user:', error)
      if (error.response?.data?.errors) {
        setErrors({
          ...error.response.data.errors,
          submit: error.response.data.message,
        })
      } else {
        setErrors({
          submit: error.response?.data?.message || 'Failed to save user'
        })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} setIsOpen={onClose} width="md:max-w-screen-md">
      <div>
        <h2 className="mb-4 text-2xl font-bold text-gray-900">
          {isEditing ? "Edit User" : "Create User"}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* First Name Field */}
            <div>
              <label htmlFor="first_name" className="mb-1 block text-sm font-medium text-gray-700">
                User First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className={`w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-brand ${
                  errors.first_name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Jane"
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-500">{errors.first_name}</p>
              )}
            </div>

            {/* First Name Field */}
            <div>
              <label htmlFor="last_name" className="mb-1 block text-sm font-medium text-gray-700">
                User Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className={`w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-brand ${
                  errors.last_name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Doe"
              />
              {errors.last_name && <p className="mt-1 text-sm text-red-500">{errors.last_name}</p>}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
                User Email <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-brand ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="jane@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Admin status + organization (create only) */}
            {!isEditing && (
              <>
                <div>
                  <span id="admin-status-label" className="mb-1 block text-sm font-medium text-gray-700">
                    Admin Status
                  </span>
                  <div role="radiogroup" aria-labelledby="admin-status-label" className="space-y-2">
                    {[
                      { value: 'none', label: 'No admin access' },
                      { value: 'org', label: 'Org Admin' },
                      { value: 'encircled', label: 'Encircled Admin' },
                    ].map((option) => (
                      <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="radio"
                          name="admin_status"
                          value={option.value}
                          checked={adminStatus === option.value}
                          onChange={(e) => {
                            setAdminStatus(e.target.value)
                            setErrors((prev) => ({ ...prev, organization_id: null }))
                          }}
                          className="text-brand focus:ring-brand"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="organization_id" className="mb-1 block text-sm font-medium text-gray-700">
                    Organization
                    {adminStatus === 'org' && <span className="text-red-500"> *</span>}
                  </label>
                  <select
                    id="organization_id"
                    name="organization_id"
                    value={organizationId}
                    onChange={(e) => {
                      setOrganizationId(e.target.value)
                      setErrors((prev) => ({ ...prev, organization_id: null }))
                    }}
                    className={`w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-brand ${
                      errors.organization_id ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">None</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                  {errors.organization_id && (
                    <p className="mt-1 text-sm text-red-500">{errors.organization_id}</p>
                  )}
                </div>

                <p className="text-sm text-gray-500">
                  This account is created without a password. The user signs in with a magic link.
                </p>
              </>
            )}

            {/* Submit Error */}
            {errors.submit && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="secondary-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={saving}>
              {saving ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className={`fa ${isEditing ? "fa-save" : "fa-plus"} mr-2`}></i>
                  {isEditing ? "Save Changes" : "Create User"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

UserFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  user: PropTypes.object,
  defaultOrganizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default UserFormModal
